/**
 * Hook for investor context switching
 */

import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useApiMutation } from '@/hooks/react-query/useApiMutation';
import { APIError } from '@/utils/api-client';
import { PortalAuthService } from '../services/auth-service';
import { investorPortalApiClient } from '../../shared/utils/api-client';
import { errorMessage, portalLog } from '../../shared/utils/portal-logger';

export interface DevLoginIdentity {
  email: string;
  display_name: string;
  investor_names: string[];
  has_fund_carry: boolean;
}

interface DevLoginIdentitiesResponse {
  identities: DevLoginIdentity[];
}

/**
 * Hook to get available investors for the current contact
 */
export function useAvailableInvestors({ enabled = true } = {}) {
  return useQuery({
    queryKey: ['portal', 'auth', 'available-investors'] as const,
    queryFn: () => PortalAuthService.getAvailableInvestors(),
    staleTime: 5 * 60 * 1000, // 5 minutes
    enabled,
  });
}

/**
 * Hook to select an investor after authentication
 * Used in new login flow
 * @param onSuccessCallback - Optional callback to run after successful selection (e.g., navigation)
 */
export function useSelectInvestor(onSuccessCallback?: () => void) {
  const queryClient = useQueryClient();

  return useApiMutation<void, APIError, { investor_id: string }>(
    ({ investor_id }) => PortalAuthService.selectInvestor(investor_id),
    {
      mutationKey: ['portal', 'auth', 'select-investor'],
      showSuccessToast: false, // navigation is the success signal
      errorMessage: 'Failed to select investor. Please try again.',
      onSuccess: () => {
        // Invalidate all queries to refetch with new investor context
        queryClient.invalidateQueries();
      },
      onError: (error, { investor_id }) => {
        portalLog.error('Failed to select investor', {
          error_message: errorMessage(error),
          investor_id,
        });
      },
      onSuccessCallback: () => onSuccessCallback?.(),
    }
  );
}

/**
 * Hook to switch investor context
 */
export function useSwitchInvestor() {
  const queryClient = useQueryClient();

  return useApiMutation<void, APIError, string>(
    (contactId) => PortalAuthService.switchInvestor(contactId),
    {
      mutationKey: ['portal', 'auth', 'switch-investor'],
      showSuccessToast: false, // page reload is the success signal
      errorMessage: 'Failed to switch investor. Please try again.',
      onSuccess: () => {
        // Invalidate all queries to refetch with new investor context
        queryClient.invalidateQueries();
        // Force a page reload to ensure all data is refreshed
        window.location.reload();
      },
      onError: (error, contactId) => {
        portalLog.error('Failed to switch investor', {
          error_message: errorMessage(error),
          contact_id: contactId,
        });
      },
    }
  );
}

/**
 * Hook to get available dev contacts
 * Only available in development mode
 */
export function useDevIdentities() {
  return useQuery({
    queryKey: ['portal', 'auth', 'dev-identities'] as const,
    queryFn: async () => {
      const response =
        await investorPortalApiClient.get<DevLoginIdentitiesResponse>(
          '/portal/auth/dev-contacts'
        );
      return response.identities;
    },
    staleTime: Number.POSITIVE_INFINITY, // Dev data doesn't change during session
    retry: false, // Don't retry if endpoint is disabled
  });
}

/**
 * Hook to perform dev login
 */
export function useDevLogin() {
  const queryClient = useQueryClient();

  return useApiMutation<void, APIError, string>(
    (email) => PortalAuthService.devLogin(email),
    {
      mutationKey: ['portal', 'auth', 'dev-login'],
      showErrorToast: false, // DevLogin renders the error inline itself
      showSuccessToast: false,
      onSuccess: () => {
        // Invalidate all queries after login
        queryClient.invalidateQueries();
      },
    }
  );
}

/**
 * Hook to verify magic link token
 * @param onSuccessCallback - Optional callback to run after successful verification (e.g., navigation)
 */
export function useVerifyMagicLink(onSuccessCallback?: () => void) {
  const queryClient = useQueryClient();

  return useApiMutation<void, APIError, string>(
    (token) => PortalAuthService.verifyMagicLink(token),
    {
      mutationKey: ['portal', 'auth', 'verify-magic-link'],
      showSuccessToast: false,
      showErrorToast: false, // MagicLinkVerify renders the error inline itself
      onSuccess: () => queryClient.invalidateQueries(),
      onSuccessCallback: () => onSuccessCallback?.(),
    }
  );
}

/**
 * Hook to verify OTP code (fallback for magic link)
 */
export function useVerifyOTP(onSuccessCallback?: () => void) {
  const queryClient = useQueryClient();

  return useApiMutation<void, APIError, { email: string; code: string }>(
    ({ email, code }) => PortalAuthService.verifyOTP(email, code),
    {
      mutationKey: ['portal', 'auth', 'verify-otp'],
      showSuccessToast: false,
      showErrorToast: false, // MagicLinkVerify renders the error inline itself
      onSuccess: () => queryClient.invalidateQueries(),
      onSuccessCallback: () => onSuccessCallback?.(),
    }
  );
}
